import { useState } from "react";
import { PhoneCall } from "lucide-react";
import api, { eur, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export default function ContactModal({ open, onOpenChange, items, subtotal, defaults }) {
  const [form, setForm] = useState({
    name: defaults?.name || "",
    email: defaults?.email || "",
    phone: defaults?.phone || "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error("Merci d'indiquer votre nom complet.");
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      toast.error("Merci d'indiquer un email valide.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact-request", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
        subtotal,
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          quantity: i.quantity,
          price: i.price,
        })),
      });
      setSent(true);
      toast.success("Votre demande a bien été envoyée.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const close = (next) => {
    onOpenChange(next);
    if (!next) setTimeout(() => setSent(false), 200);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-cream max-w-md" data-testid="contact-modal">
        {sent ? (
          <div className="text-center py-6">
            <PhoneCall className="h-8 w-8 text-copper mx-auto mb-3" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl text-ink mb-2">Merci !</h2>
            <p className="text-sm text-graphite">
              Votre demande a bien été transmise. Nous revenons vers vous rapidement pour finaliser votre commande.
            </p>
            <button
              type="button"
              onClick={() => close(false)}
              className="mt-6 bg-ink text-cream px-6 py-3 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors"
            >
              Fermer
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-ink">Être recontacté(e)</DialogTitle>
              <DialogDescription className="text-graphite text-sm">
                Le paiement en ligne est momentanément indisponible. Laissez-nous vos coordonnées,
                nous vous recontactons pour finaliser votre commande.
              </DialogDescription>
            </DialogHeader>

            <div className="max-h-40 overflow-y-auto border border-stone rounded-sm divide-y divide-stone" data-testid="contact-modal-items">
              {items.map((i) => (
                <div key={i.product_id} className="flex items-center gap-3 p-2.5">
                  {i.image && <img src={i.image} alt={i.name} className="h-10 w-10 object-cover bg-stone rounded-sm shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-ink truncate">{i.name}</p>
                    <p className="text-xs text-ash">Qté {i.quantity}</p>
                  </div>
                  <span className="font-mono text-xs text-ink shrink-0">{eur(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-ash">Nom complet *</label>
                <Input data-testid="contact-name" value={form.name} onChange={setField("name")} placeholder="Jean Dupont" className="mt-1.5 rounded-sm border-stone bg-white h-11" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-ash">Email *</label>
                <Input data-testid="contact-email" type="email" value={form.email} onChange={setField("email")} placeholder="jean@email.com" className="mt-1.5 rounded-sm border-stone bg-white h-11" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-ash">Téléphone</label>
                <Input data-testid="contact-phone" type="tel" value={form.phone} onChange={setField("phone")} placeholder="06 12 34 56 78" className="mt-1.5 rounded-sm border-stone bg-white h-11" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-ash">Message</label>
                <Textarea data-testid="contact-message" value={form.message} onChange={setField("message")} placeholder="Précisions utiles (facultatif)" className="mt-1.5 rounded-sm border-stone bg-white" rows={3} />
              </div>
              <DialogFooter>
                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={sending}
                  className="w-full bg-ink text-cream py-3.5 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-60"
                >
                  {sending ? "Envoi..." : "Envoyer ma demande"}
                </button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
